'use client';

import { useId, useMemo } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiHistoryLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';
import type { ChangelogEntrySummary } from '@mentorivo/shared';

import { formatDate } from '@/lib/format';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * Değişiklikler sayfasının açılışı.
 *
 * ── Neden hazır bir ışık katmanı YOK ──────────────────────────────────────
 *
 * Bu depodaki her hero bir zemin efekti taşıyor: ajan sayfasında küre
 * (`orb`), blogda ışın alanı (`beams`), fiyatta dalga alanı (`waves`),
 * anasayfada gradyan dalgalar. Altıncı bir WebGL katmanı eklemek kolay
 * olurdu ve burada YANLIŞ olurdu — iki sebeple:
 *
 *   1. Bu sayfanın konusu ZAMAN. Soyut bir ışık alanı onu anlatmıyor; hero,
 *      altındaki çizelgenin ne olduğunu söylemeli.
 *   2. Bu depoda WebGL katmanlarının ölçülmüş bir bedeli var (`ogl`in
 *      `setSize`i CSS genişliğini px olarak yazıyor, kap genişleyince kenarda
 *      boş şerit bırakıyor). Bir sayfanın kimliği için o riski almaya değmez.
 *
 * Yerine geçen şey KENDİ VERİSİ: hero'nun altındaki ince hat, son on iki ayın
 * gerçek yayın günlerini işaretliyor. Uydurulmuş bir grafik değil — aşağıdaki
 * listenin aynı kayıtları. Ziyaretçi "bu ürün ne sıklıkla güncelleniyor"
 * sorusunun cevabını sayfayı kaydırmadan görüyor.
 *
 * Hat `aria-hidden`: taşıdığı bilginin tamamı altındaki listede metin olarak
 * var ve ekran okuyucuya otuz tarih işaretini tek tek okutmak, aynı şeyi iki
 * kez söylemenin en yorucu hâli olurdu. Onun yerine iki cümlelik künye
 * (`.chero-fact`) metin olarak duruyor.
 *
 * ── İskelet ───────────────────────────────────────────────────────────────
 *
 *   header payı → rozet → iki satırlık başlık → cümle → künye + yayın hattı
 *
 * Tipografi ödünç: başlık ve açıklama anasayfanın hero sınıflarını
 * (`.new-hero-*`) kullanıyor — kopyalanmış bir ölçek değil AYNI ölçek. O
 * sınıflar vendor CSS'ine `[data-v-…]` seçicisiyle bağlı, bu yüzden nitelik
 * markup'ta tekrarlanıyor (aynı gerekçe: components/blog-hero.tsx).
 *
 * Zemin AÇIK — blog hero'suyla aynı aile. Sayfa bir okuma yüzeyi ve tek koyu
 * şerit yine kapanış (`cta.tsx`).
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Bölüm ekranın tepesinde açılıyor; `whileInView` beklemesi anlamsız,
 * `animate` ile doğrudan oynuyor. Sıra okuma sırası: rozet → başlık → cümle →
 * künye → hat. Hat EN SON ve soldan sağa çiziliyor: zamanın yönü.
 *
 * Blur yalnızca başlıkta (§6 bütçesi).
 */

/* Vendor CSS'inin kapsam niteliği. Anasayfanın hero sınıfları `[data-v-…]`
   seçicisine bağlı; nitelik olmadan kural hiç uygulanmıyor. */
const V = 'd0b8df6f';

/** Hattın kapsadığı pencere. Bir yıl: mevsimlik bir ritim okunabilir olsun. */
const WINDOW_DAYS = 365;
const DAY_MS = 24 * 60 * 60 * 1000;

const sceneV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.loose, delayChildren: 0.05 } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Bölümdeki tek blur: sayfanın adı. */
const titleV: Variants = {
  hidden: { opacity: 0, y: 14, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

/* Hat soldan sağa çiziliyor — `scaleX`, `width` değil (DESIGN.md §1:
   yalnız transform/opacity). `transform-origin` CSS'te sol kenarda. */
const railV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: {
    opacity: 1,
    scaleX: 1,
    transition: { duration: dur.slow, ease: ease.out, delay: 0.1 },
  },
};

/* İşaretler hat çizildikten SONRA beliriyor: önce zaman ekseni kuruluyor,
   sonra üstündeki olaylar. Ters sırada olsaydı işaretler bir an boşlukta
   asılı kalırdı. */
const ticksV: Variants = {
  hidden: {},
  show: { transition: { delayChildren: dur.slow * 0.55, staggerChildren: stagger.tight } },
};

const tickV: Variants = {
  hidden: { opacity: 0, scaleY: 0.2 },
  show: { opacity: 1, scaleY: 1, transition: springFade(spring.snap) },
};

/* En yeni yayının halkası: bir kez açılıp duruyor, DÖNGÜ YOK. Nabız gibi
   atan bir işaret dikkati sürekli yukarıda tutardı; buranın işi sayfayı
   açmak, sonra kenara çekilmek. */
const pulseV: Variants = {
  hidden: { opacity: 0, scale: 0.4 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { ...springFade(spring.bouncy), delay: dur.slow * 0.9 },
  },
};

type Tick = {
  slug: string;
  /** Hat üzerindeki konum, 0 (bir yıl önce) → 100 (en yeni yayın). */
  at: number;
  newest: boolean;
};

export function ChangelogHero({ entries }: { entries: ChangelogEntrySummary[] }) {
  const t = useTranslations('changelog.hero');
  const locale = useLocale();
  const uid = useId();

  /**
   * Yayın günlerini hat üzerindeki konumlara çevirir.
   *
   * Pencere EN YENİ kayıttan geriye bir yıl. "Bugünden geriye" değil ve fark
   * önemli: uzun bir sessizlikten sonra bakıldığında bugüne göre hesaplanan
   * bir hat, bütün işaretleri sola yığıp sağ yarısını bomboş bırakırdı —
   * yani ürünü ölmüş gösterirdi. Oysa anlatılmak istenen şey ürünün
   * ETKİNLİK RİTMİ; sessizliğin kendisi listenin tarihlerinde zaten yazılı.
   *
   * Pencerenin dışında kalan kayıtlar hatta görünmüyor. Kırpmak yerine sola
   * yığmak, orada olmayan bir yoğunluk çizerdi.
   */
  const ticks = useMemo<Tick[]>(() => {
    const newest = entries[0];
    if (!newest) return [];

    const end = new Date(newest.releasedAt).getTime();
    const start = end - WINDOW_DAYS * DAY_MS;

    return entries.flatMap((entry) => {
      const time = new Date(entry.releasedAt).getTime();
      if (!Number.isFinite(time) || time < start) return [];

      /* `end === start` yalnız tek kayıt varsa mümkün değil (start hep bir yıl
         geride) ama bölme sıfıra düşerse `NaN` bir işareti hattın dışına
         atardı — açıkça 100 dönmek, sessiz bir NaN'dan iyi. */
      const span = end - start;
      const at = span > 0 ? ((time - start) / span) * 100 : 100;

      return [{ slug: entry.slug, at, newest: entry.slug === newest.slug }];
    });
  }, [entries]);

  const newest = entries[0] ?? null;

  return (
    <section className="chero" aria-labelledby={`${uid}-title`}>
      <div className="chero-body">
        {/* Header `position: fixed`, akışta yer kaplamıyor — payı burada. */}
        <div className="chero-space" aria-hidden="true" />

        <div className="grid-12-container">
          <div className="grid-item-2fr" />

          <div className="grid-item-8fr">
            <div className="chero-frame">
              <motion.div className="chero-copy" variants={sceneV} initial="hidden" animate="show">
                <motion.p className="chero-eyebrow" variants={itemV}>
                  <RiHistoryLine size={18} aria-hidden="true" />
                  {t('eyebrow')}
                </motion.p>

                <motion.h1
                  className="new-hero-title"
                  data-v-d0b8df6f={V}
                  id={`${uid}-title`}
                  variants={titleV}
                >
                  {/* İki satır da span: kırılma noktası viewport genişliğine
                      bağlı kalmıyor, her kademede iki satır. */}
                  <span
                    className="new-hero-title-line new-hero-title-line--lead"
                    data-v-d0b8df6f={V}
                  >
                    {t('titleLine1')}
                  </span>
                  <span
                    className="new-hero-title-line new-hero-title-line--accent"
                    data-v-d0b8df6f={V}
                  >
                    {t('titleLine2')}
                  </span>
                </motion.h1>

                <motion.p className="new-hero-description" data-v-d0b8df6f={V} variants={itemV}>
                  {t('description')}
                </motion.p>

                {/* Künye ve hat YALNIZ kayıt varken. Boş bir çizelgenin
                    üstünde "son yayın: —" yazmak, sayfanın ilk cümlesini bir
                    eksikliğe çevirirdi; o durumda listenin kendi boş hâli
                    konuşuyor (`components/changelog-list.tsx`). */}
                {newest ? (
                  <motion.div className="chero-facts" variants={itemV}>
                    <p className="chero-fact">
                      <span className="chero-fact-label">{t('lastRelease')}</span>
                      <time className="chero-fact-value" dateTime={newest.releasedAt}>
                        {formatDate(newest.releasedAt, locale)}
                      </time>
                    </p>
                    <p className="chero-fact">
                      <span className="chero-fact-label">{t('windowLabel')}</span>
                      <span className="chero-fact-value">
                        {t('windowValue', { count: ticks.length })}
                      </span>
                    </p>
                  </motion.div>
                ) : null}
              </motion.div>

              {ticks.length > 0 ? (
                <motion.div
                  className="chero-rail"
                  aria-hidden="true"
                  initial="hidden"
                  animate="show"
                >
                  <motion.span className="chero-rail-line" variants={railV} />

                  <motion.span className="chero-rail-ticks" variants={ticksV}>
                    {ticks.map((tick) => (
                      <motion.span
                        key={tick.slug}
                        className="chero-tick"
                        data-newest={tick.newest}
                        /* Fiziksel `left` bilerek: `translate(-50%)` ortalaması
                           onu istiyor. Sağdan sola dilde hattın TAMAMI CSS'te
                           aynalanıyor (`.chero-rail` → `scale: -1 1`). */
                        style={{ left: `${tick.at}%` }}
                        variants={tickV}
                      >
                        {/* En yeni yayının halkası. Azaltılmış hareket elle
                            kontrol EDİLMİYOR ve edilmemeli: bu bir döngü ya
                            da parallax değil, tek seferlik bir ölçek geçişi —
                            kök layout'taki `<MotionConfig reducedMotion="user">`
                            onu zaten durduruyor (DESIGN.md §5).

                            `reduce ? undefined : …` ile koşullu varyant
                            vermek de ayrıca YANLIŞ olurdu: bu depoda koşullu
                            render'ın hidrasyonu kırdığı ölçüldü. */}
                        {tick.newest ? (
                          <motion.span className="chero-tick-ring" variants={pulseV} />
                        ) : null}
                      </motion.span>
                    ))}
                  </motion.span>
                </motion.div>
              ) : null}
            </div>
          </div>

          <div className="grid-item-2fr" />
        </div>
      </div>
    </section>
  );
}
